import { useState } from 'react'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LoaderCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { COBALT_SETTINGS_STALE_TIME_MS } from '@/config/constants'
import { requireAuthToken } from '@/lib/api'
import { cobaltConnectionKeys } from '@/lib/queryKeys'
import { US_TIMEZONE_OPTIONS } from '@/utils/time'
import {
  fetchCobaltProviders,
  updateCobaltProvider,
} from './cobaltProvidersApi'
import { fetchCobaltOrgSettings } from './api'
import type { CobaltProvider, CobaltProviderDay } from './types'
import { CobaltProviderTable } from './CobaltProviderTable'
import { CobaltProviderHours } from './CobaltProviderHours'
import { CobaltProviderDiscoveryStatus } from './CobaltProviderDiscoveryStatus'
import { useCobaltProviderDiscovery } from './useCobaltProviderDiscovery'

interface Props {
  autoDiscover?: boolean
  onDiscoveryStarted?: () => void
}

const EMPTY_HOURS: CobaltProviderDay[] = []
const EMPTY_PROVIDERS: CobaltProvider[] = []

export function CobaltProvidersPanel(props: Props) {
  const { organization } = useOrganization()
  if (!organization?.id) return null
  // Switching organizations discards edits and resumes only that org's job.
  return (
    <ProviderDirectory
      key={organization.id}
      orgId={organization.id}
      {...props}
    />
  )
}

function ProviderDirectory({
  orgId,
  autoDiscover,
  onDiscoveryStarted,
}: Props & { orgId: string }) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [changes, setChanges] = useState<
    Record<string, CobaltProvider['status']>
  >({})
  const [message, setMessage] = useState('')
  const discovery = useCobaltProviderDiscovery({
    orgId,
    getToken,
    autoDiscover,
    onDiscoveryStarted,
  })
  const providersKey = cobaltConnectionKeys.providers(orgId)
  const providers = useQuery({
    queryKey: providersKey,
    queryFn: async ({ signal }) =>
      fetchCobaltProviders(await requireAuthToken(getToken), signal),
    staleTime: 0,
    retry: false,
  })
  const settings = useQuery({
    queryKey: cobaltConnectionKeys.settings(orgId),
    queryFn: async ({ signal }) =>
      fetchCobaltOrgSettings(await requireAuthToken(getToken), signal),
    staleTime: COBALT_SETTINGS_STALE_TIME_MS,
    retry: false,
  })
  const directory = providers.data?.providers ?? EMPTY_PROVIDERS
  const defaultHours = providers.data?.default_working_hours ?? EMPTY_HOURS
  // Saving selected providers also initializes any missing schedules. Relay
  // rechecks upstream hours before writing, preserving newly configured hours.
  const pendingUpdates: Record<string, CobaltProvider['status']> = {
    ...Object.fromEntries(
      directory
        .filter(
          (provider) =>
            defaultHours.length > 0 &&
            (changes[provider.id] ?? provider.status) === 'active' &&
            !provider.hours?.length
        )
        .map((provider) => [provider.id, 'active'] as const)
    ),
    ...changes,
  }
  // Cobalt defines shift clock times in the clinic's EMR timezone, even
  // though provider records also contain a separate timezone field.
  // https://docs.usecobalt.com/api-reference/providers/get#schedule-structure
  const timezone = settings.data?.timezone
  const timezoneLabel =
    US_TIMEZONE_OPTIONS.find((option) => option.value === timezone)?.label ??
    timezone ??
    'eCW instance local time'
  const save = useMutation({
    mutationFn: async () => {
      const token = await requireAuthToken(getToken)
      const saved = new Set<string>()
      let failed = false
      // Keep request volume bounded when an organization selects many providers.
      for (const [id, status] of Object.entries(pendingUpdates)) {
        try {
          await updateCobaltProvider(token, id, status)
          saved.add(id)
        } catch {
          failed = true
        }
      }
      setChanges((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([id]) => !saved.has(id))
        )
      )
      return failed
    },
    onSuccess: (failed) =>
      setMessage(
        failed
          ? 'Some providers could not be updated. Your remaining selections are ready to retry.'
          : 'Provider selections saved.'
      ),
    onError: () =>
      setMessage('Provider selections could not be saved. Try again.'),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: providersKey, exact: true }),
  })

  const { busy } = discovery
  const statusMessage = message || discovery.message
  const locked = busy || save.isPending
  const startDiscovery = () => {
    setMessage('')
    discovery.startDiscovery()
  }

  return (
    <section
      className="grid gap-3 rounded-xl border border-border p-4"
      aria-label="Cobalt providers">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">Providers to sync</h3>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          aria-label={
            busy
              ? 'Discovering providers…'
              : discovery.hasRetainedJob
                ? 'Discovery awaiting status'
                : 'Refresh providers from eClinicalWorks'
          }
          disabled={
            locked ||
            discovery.hasRetainedJob ||
            Object.keys(changes).length > 0
          }
          onClick={startDiscovery}>
          {busy && (
            <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          )}
          {busy
            ? 'Discovering…'
            : discovery.hasRetainedJob
              ? 'Awaiting status'
              : 'Refresh'}
        </Button>
      </div>
      {providers.data && (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            All times in {timezoneLabel}
            {timezone && ' · eCW instance'}
          </p>
          {defaultHours.length > 0 && (
            <div>
              <span className="font-medium">Default schedule</span>
              <CobaltProviderHours hours={defaultHours} />
            </div>
          )}
        </div>
      )}
      <CobaltProviderDiscoveryStatus
        discovery={discovery}
        message={statusMessage}
        locked={locked || Object.keys(changes).length > 0}
        onRestart={startDiscovery}
      />
      {providers.isLoading ? (
        <p role="status">Loading providers…</p>
      ) : providers.isError ? (
        <div role="alert">
          Providers could not be loaded.{' '}
          <Button variant="link" onClick={() => void providers.refetch()}>
            Retry providers
          </Button>
        </div>
      ) : (
        <>
          <Input
            aria-label="Search Cobalt providers"
            placeholder="Search name, NPI, or EHR ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <CobaltProviderTable
            providers={directory}
            defaultHours={defaultHours}
            search={search}
            changes={changes}
            locked={locked}
            discovering={busy}
            onChange={setChanges}
          />
          <Button
            className="justify-self-end"
            disabled={locked || !Object.keys(pendingUpdates).length}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Saving providers…' : 'Save provider selections'}
          </Button>
        </>
      )}
      <p className="text-xs text-muted-foreground">
        Entity syncs must also be enabled in Cobalt.
      </p>
    </section>
  )
}
